// native
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { finalize } from 'rxjs/operators';

// addon
import { TranslateService } from '@ngx-translate/core';

// component
import { DeviceFormComponent } from 'app/devices/device-form/device-form.component';

// service
import { DevicesService } from 'app/core/services/devices.service';
import { ErrorService } from 'app/core/services/error.service';
import { DialogService } from 'app/core/services/dialog.service';

// models
import { Device } from '../../../models';
import { DeviceMode, DeviceStepResult } from '../../home-management.types';

// constants
import { DEVICE_TYPE } from '../../../constants';

@Component({
  selector: 'app-home-device-step',
  templateUrl: './device-step.component.html',
  standalone: false
})
export class DeviceStepComponent implements OnInit {
  @Input() initial: DeviceStepResult | null = null;
  @Output() next: EventEmitter<DeviceStepResult> = new EventEmitter<DeviceStepResult>();
  @Output() back: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild(DeviceFormComponent) deviceForm: DeviceFormComponent;

  mode: DeviceMode = 'existing';

  // assign existing
  availableDevices: Device[] = [];
  occupiedDevices: Device[] = [];
  deviceControl = new FormControl<Device | null>(null);
  selectedDevice: Device | null = null;
  reallocate = false;

  // create new (delegated to the shared DeviceFormComponent, locked to home).
  // The device is NOT created here - the captured form values are created on
  // the wizard's final submit.
  readonly homeDeviceType = DEVICE_TYPE.HOME;
  initialNewDevice: Device | null = null;
  createFormValid = false;

  isLoading = false;

  constructor(
    private devicesService: DevicesService,
    private errorService: ErrorService,
    private dialogService: DialogService,
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    this.isLoading = true;
    this.devicesService.getAllUnpaginated().pipe(
      finalize(() => this.isLoading = false)
    ).subscribe((devices: Device[]) => {
      const homeDevices = devices.filter(d => d.device_type === DEVICE_TYPE.HOME);
      this.availableDevices = homeDevices.filter(d => !this.isOccupied(d));
      this.occupiedDevices = homeDevices.filter(d => this.isOccupied(d));
      this.restore();
    }, error => this.errorService.handleError(error));
  }

  private restore(): void {
    if (!this.initial)
      return;

    this.mode = this.initial.mode;
    if (this.initial.mode === 'existing') {
      this.selectedDevice = this.initial.device;
      this.deviceControl.setValue(this.initial.device);
      this.reallocate = this.initial.reallocate;
    } else {
      // Restore previously entered (but not yet created) device values so the
      // embedded form can be pre-filled.
      this.initialNewDevice = this.initial.newDevice;
    }
  }

  setMode(mode: DeviceMode): void {
    this.mode = mode;
  }

  compareDevices = (a: Device, b: Device): boolean => a?.id === b?.id;

  isOccupied(device: Device): boolean {
    return !!device.has_pending_scheduled_tests || !!device.has_running_test;
  }

  onExistingSelect(device: Device): void {
    if (!this.isOccupied(device)) {
      this.selectedDevice = device;
      this.reallocate = false;
      return;
    }

    this.dialogService.openConfirm({
      action: this.translateService.instant('reallocateDevice'),
      message: this.translateService.instant('reallocateDevicePrompt'),
      text: this.translateService.instant('device') + ': ' + device.name,
      confirmText: this.translateService.instant('yes'),
      cancelText: this.translateService.instant('no')
    }).then(result => {
      if (result.confirmed) {
        this.selectedDevice = device;
        this.reallocate = true;
      } else {
        this.selectedDevice = null;
        this.reallocate = false;
        this.deviceControl.setValue(null);
      }
    });
  }

  get canProceed(): boolean {
    if (this.mode === 'existing')
      return !!this.selectedDevice;
    // Create-new: enabled once the embedded form is valid. The device itself is
    // only created on the wizard's final submit.
    return this.createFormValid;
  }

  onNext(): void {
    if (this.mode === 'new') {
      if (!this.deviceForm || !this.createFormValid)
        return;

      // Capture the form values; the device is created on final submit.
      this.next.emit({
        mode: 'new',
        device: null,
        newDevice: this.deviceForm.buildBody(),
        pairingCode: null,
        reallocate: false
      });
      return;
    }

    if (!this.selectedDevice)
      return;

    this.next.emit({
      mode: 'existing',
      device: this.selectedDevice,
      newDevice: null,
      pairingCode: this.selectedDevice.code ?? null,
      reallocate: this.reallocate
    });
  }
}
